import { cn } from '@invent/wl-ui-kit-next';
import {
  Avatar,
  AvatarFallback,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger
} from '@invent/wl-ui-kit-next/ui';
import { Menu } from 'lucide-react';
import * as React from 'react';
import { Link, NavLink, Outlet } from 'react-router-dom';

import { Lockup, ZoneProvider, useZoneRoot } from '@/design';
import { EmailVerificationBanner, initialsFromEmail, useAuth } from '@/domains/auth';
import { PORTAL_PATHS } from '@/domains/survey';
import { PORTAL_NAV } from './nav';

/**
 * The Advisor Portal's shell: a slim white bar, the address prompt, and the page on the Off White
 * ground (the two-portals design, §3.2).
 *
 * The bar holds the lockup, the portal's entries as text links — the current one underlined in
 * Sapphire — and the account menu. Below 1024 px the entries fold into a sheet behind "Menu", so a
 * laptop keeps the full bar and a tablet a usable one.
 *
 * A survey takes the bar over (`FocusBar`, the design's §3.3 focus mode): while one holds it, the
 * entries, "Menu" and the account menu give way to the slot the survey draws its name, its save
 * state and "Finish later" into, and the lockup stays as the way home.
 *
 * It is the Advisor Portal's alone: nothing in it names a Team Portal route or imports from
 * `zones/team`. A staff account never reaches it — the zone's guard shows the boundary page.
 *
 * `children` stands in for the route outlet on the one page drawn outside the route tree's
 * shell, Help, which is reachable signed out and so decides its own frame.
 */
export function PortalShell({ children }: { children?: React.ReactNode }) {
  useZoneRoot('portal');
  const [focusSlot, setFocusSlot] = React.useState<Nullable<HTMLElement>>(null);
  const [focusHolders, setFocusHolders] = React.useState(0);
  const onFocus = React.useCallback(() => {
    setFocusHolders((held) => held + 1);
    return () => setFocusHolders((held) => held - 1);
  }, []);

  return (
    <ZoneProvider zone="portal" focusSlot={focusSlot} onFocus={onFocus}>
      <div className="flex min-h-screen flex-col bg-background text-foreground">
        <PortalBar focused={focusHolders > 0} slotRef={setFocusSlot} />
        <EmailVerificationBanner />
        <main className="flex flex-1 flex-col">{children ?? <Outlet />}</main>
      </div>
    </ZoneProvider>
  );
}

const ENTRY =
  'flex h-full items-center border-b-2 text-[0.9375rem] outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring';

function PortalBar({
  focused,
  slotRef
}: {
  focused: boolean;
  slotRef: (element: Nullable<HTMLElement>) => void;
}) {
  return (
    <header className="sticky top-0 z-30 border-b border-border bg-white">
      {/* A 16 px gutter and closer spacing on a phone: at 24 px a survey's save state and
          "Finish later" ran past a 375 px screen's edge. */}
      <div className="mx-auto flex h-16 w-full max-w-[1440px] items-center gap-3 px-4 sm:gap-8 sm:px-6 lg:px-10">
        <Link
          to={PORTAL_PATHS.home}
          className="shrink-0 rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Lockup zoneName="Advisor Portal" />
        </Link>

        {/* Always mounted, so a page that takes the bar finds its slot on its first effect. */}
        <div
          ref={slotRef}
          className={focused ? 'flex min-w-0 flex-1 items-center gap-4 sm:gap-6' : 'hidden'}
        />

        {!focused && (
          <>
            <PortalEntries />
            <PortalActions />
          </>
        )}
      </div>
    </header>
  );
}

function PortalEntries() {
  return (
    <nav aria-label="Advisor Portal" className="hidden h-full lg:block">
      <ul className="flex h-full items-stretch gap-7">
        {PORTAL_NAV.map((entry) => (
          <li key={entry.to}>
            <NavLink
              to={entry.to}
              end={entry.end}
              className={({ isActive }) =>
                cn(
                  ENTRY,
                  isActive
                    ? 'border-[var(--color-sapphire)] font-medium text-[var(--color-royal-blue)]'
                    : 'border-transparent text-muted-foreground hover:text-[var(--color-royal-blue)]'
                )
              }
            >
              {entry.label}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}

function PortalActions() {
  const [sheetOpen, setSheetOpen] = React.useState(false);

  return (
    <div className="ml-auto flex items-center gap-2">
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetTrigger asChild>
          <Button type="button" variant="ghost" size="sm" className="gap-2 lg:hidden">
            <Menu className="size-4" aria-hidden />
            {/* The word gives way on a phone, where the bar has no room for it; the name stays. */}
            <span className="max-sm:sr-only">Menu</span>
          </Button>
        </SheetTrigger>
        <SheetContent side="right" className="w-72 p-0">
          <SheetTitle className="sr-only">Advisor Portal</SheetTitle>
          <nav aria-label="Advisor Portal" className="p-4 pt-12">
            <ul className="grid gap-1">
              {PORTAL_NAV.map((entry) => (
                <li key={entry.to}>
                  <NavLink
                    to={entry.to}
                    end={entry.end}
                    onClick={() => setSheetOpen(false)}
                    className={({ isActive }) =>
                      cn(
                        'block rounded-md px-3 py-2.5 text-base',
                        isActive
                          ? 'bg-[var(--color-slight-blue)] font-medium text-[var(--color-royal-blue)]'
                          : 'text-muted-foreground hover:text-[var(--color-royal-blue)]'
                      )
                    }
                  >
                    {entry.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>
        </SheetContent>
      </Sheet>
      <AccountMenu />
    </div>
  );
}

/** survey-api knows an address and a role — there is no name to show, so the address is it. */
function AccountMenu() {
  const { user, signout } = useAuth();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          aria-label="Account menu"
          className="h-10 gap-3 rounded-full px-1.5 text-muted-foreground hover:text-[var(--color-royal-blue)] xl:pl-3"
        >
          <span className="hidden max-w-[16rem] truncate text-sm font-normal xl:inline">
            {user?.email}
          </span>
          <Avatar className="size-8">
            {/* Sapphire fill, white initials: 6.5:1. */}
            <AvatarFallback className="bg-[var(--color-sapphire)] text-xs font-medium text-white">
              {initialsFromEmail(user?.email ?? null)}
            </AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="grid gap-0.5">
          <span className="text-sm font-medium break-all">{user?.email}</span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to={PORTAL_PATHS.account}>Account</Link>
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={signout}>Log out</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
